/**
 * The size an image frame is created at: a fixed width and the image's
 * aspect, with the height held between the backend's floor and cap
 * (`IMAGE_FRAME_*` in `backend/apps/canvas/frame_defaults.py`). Without a
 * known pixel size the frame takes the image default (400×300).
 *
 * Every image-frame creator sends this size and stores the pixel size in
 * `content.images[i]`, so the card and the opened image keep their aspect.
 */

import { getFrameDefaultSize } from "@/lib/frame-defaults";
import type { ImagePixelSize } from "@/lib/image-natural-size";
import type { FrameSize } from "@/lib/workspace-view-adapter";

const IMAGE_FRAME_WIDTH = 400;
const IMAGE_FRAME_MIN_HEIGHT = 240;
const IMAGE_FRAME_MAX_HEIGHT = 700;

/** Both sides when they are positive, else `null` (an unmeasured upload is 0×0). */
export function imagePixelSize(image: {
  width?: number;
  height?: number;
}): ImagePixelSize | null {
  const { width, height } = image;
  return width && height && width > 0 && height > 0 ? { width, height } : null;
}

export function imageFrameSize(image: {
  width?: number;
  height?: number;
}): FrameSize {
  const size = imagePixelSize(image);
  if (!size) {
    const { w, h } = getFrameDefaultSize("image");
    return { width: w, height: h };
  }
  const height = (IMAGE_FRAME_WIDTH * size.height) / size.width;
  return {
    width: IMAGE_FRAME_WIDTH,
    height: Math.round(
      Math.min(
        Math.max(height, IMAGE_FRAME_MIN_HEIGHT),
        IMAGE_FRAME_MAX_HEIGHT,
      ),
    ),
  };
}

/**
 * The pixel size of a local image file, decoded here before it uploads, or
 * `null` when the browser cannot decode it.
 */
export async function readImageFileSize(
  file: Blob,
): Promise<ImagePixelSize | null> {
  if (typeof createImageBitmap !== "function") return null;
  try {
    const bitmap = await createImageBitmap(file);
    const size = imagePixelSize(bitmap);
    bitmap.close();
    return size;
  } catch {
    return null;
  }
}
